import { authService } from '@/features/auth/services/authService';
import { supabase } from '@tickitacka/shared';

// 지원되는 엔티티 타입
export type EntityType = 'feedback' | 'daily_scrum_item';

export const emojiReactionService = {
    /**
     * 이모지 리액션을 추가합니다.
     * @param entityType 엔티티 타입 ('feedback' 또는 'daily_scrum_item')
     * @param entityId 엔티티 ID
     * @param emojiCode 이모지 코드
     */
    async addEmojiReaction(
        entityType: EntityType,
        entityId: string,
        emojiCode: string
    ) {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');
            
            if (entityType === 'feedback') {
                const { data, error } = await supabase
                    .from('feedback_emoji_reaction')
                    .insert({
                        feedback_id: entityId,
                        emoji_code: emojiCode,
                        register_id: user.id,
                        register_date: new Date().toISOString(),
                        update_date: new Date().toISOString(),
                        enable: true
                    })
                    .select()
                    .single();
                    
                if (error) throw error;
                return data;
            } else {
                // daily_scrum_item
                const { data, error } = await supabase
                    .from('daily_scrum_item_emoji_reaction')
                    .insert({
                        daily_scrum_item_id: entityId,
                        emoji_code: emojiCode,
                        register_id: user.id,
                        register_date: new Date().toISOString(),
                        update_date: new Date().toISOString(),
                        enable: true
                    })
                    .select()
                    .single();
                    
                if (error) throw error;
                return data;
            }
        } catch (error) {
            console.error(
                `이모지 리액션 추가 실패 (${entityType}:${entityId})`,
                error
            );
            throw error;
        }
    },

    /**
     * 이모지 리액션을 삭제합니다.
     * @param entityType 엔티티 타입 ('feedback' 또는 'daily_scrum_item')
     * @param entityId 엔티티 ID
     * @param emojiCode 이모지 코드
     */
    async deleteEmojiReaction(
        entityType: EntityType,
        entityId: string,
        emojiCode: string
    ) {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            if (entityType === 'feedback') {
                const { error } = await supabase
                    .from('feedback_emoji_reaction')
                    .delete()
                    .eq('feedback_id', entityId)
                    .eq('register_id', user.id)
                    .eq('emoji_code', emojiCode);

                if (error) throw error;
            } else {
                // daily_scrum_item
                const { error } = await supabase
                    .from('daily_scrum_item_emoji_reaction')
                    .delete()
                    .eq('daily_scrum_item_id', entityId)
                    .eq('register_id', user.id)
                    .eq('emoji_code', emojiCode);

                if (error) throw error;
            }
            
            return true;
        } catch (error) {
            console.error(
                `이모지 리액션 삭제 실패 (${entityType}:${entityId})`,
                error
            );
            throw error;
        }
    },

    /**
     * 특정 엔티티에 대한 이모지 리액션 목록을 조회합니다.
     * @param entityType 엔티티 타입 ('feedback' 또는 'daily_scrum_item')
     * @param entityId 엔티티 ID
     */
    async getEmojiReactions(entityType: EntityType, entityId: string) {
        try {
            const selectQuery = `
                id,
                emoji_code,
                register_id,
                register_date,
                user:user!register_id (
                    id,
                    name,
                    profile_image_url
                )
            `;
            
            if (entityType === 'feedback') {
                const { data, error } = await supabase
                    .from('feedback_emoji_reaction')
                    .select(selectQuery)
                    .eq('feedback_id', entityId)
                    .eq('enable', true);

                if (error) throw error;
                return data || [];
            } else {
                // daily_scrum_item
                const { data, error } = await supabase
                    .from('daily_scrum_item_emoji_reaction')
                    .select(selectQuery)
                    .eq('daily_scrum_item_id', entityId)
                    .eq('enable', true);

                if (error) throw error;
                return data || [];
            }
        } catch (error) {
            console.error(
                `이모지 리액션 조회 실패 (${entityType}:${entityId})`,
                error
            );
            throw error;
        }
    },
};
